import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface LedgerEntry {
  id: string;
  entry_type: string;
  account: 'client' | 'escrow' | 'platform' | 'vendor' | 'tax' | 'cc_fees' | 'settlement';
  amount_cents: number;
  status: string;
  description: string | null;
  correlation_id: string | null;
  created_at: string;
  source_type: string;
  source_transaction_id: string | null;
  source_adjustment_id: string | null;
}

export interface JobBalance {
  job_id: string;
  purchase_order_id: string | null;
  client_balance_cents: number;
  escrow_balance_cents: number;
  platform_earned_cents: number;
  vendor_balance_cents: number;
  tax_balance_cents: number;
  cc_fees_balance_cents: number;
  settlement_balance_cents: number;
  total_balance_cents: number;
  is_balanced: boolean;
  entry_count: number;
  last_entry_at: string | null;
}

export interface JobReconciliation {
  reconciliation_status: string | null;
  pending_refund_cents: number | null;
  pending_charge_cents: number | null;
}

export function useJobLedger(jobId: string | null) {
  const entriesQuery = useQuery({
    queryKey: ['job-ledger-entries', jobId],
    queryFn: async () => {
      if (!jobId) return [];
      
      const { data, error } = await supabase
        .from('financial_ledger')
        .select('*')
        .eq('job_id', jobId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data as LedgerEntry[];
    },
    enabled: !!jobId,
  });

  const balanceQuery = useQuery({
    queryKey: ['job-balance-summary', jobId],
    queryFn: async () => {
      if (!jobId) return null;
      
      const { data, error } = await supabase
        .from('job_balance_summary')
        .select('*')
        .eq('job_id', jobId)
        .maybeSingle();
      
      if (error) throw error;
      return data as JobBalance | null;
    },
    enabled: !!jobId,
  });

  const reconciliationQuery = useQuery({
    queryKey: ['job-reconciliation', jobId],
    queryFn: async () => {
      if (!jobId) return null;
      
      const { data, error } = await supabase
        .from('job_financial_reconciliations')
        .select('reconciliation_status, pending_refund_cents, pending_charge_cents')
        .eq('job_id', jobId)
        .maybeSingle();
      
      if (error) throw error;
      return data as JobReconciliation | null;
    },
    enabled: !!jobId,
  });

  return {
    entries: entriesQuery.data || [],
    balance: balanceQuery.data,
    reconciliation: reconciliationQuery.data,
    isLoading: entriesQuery.isLoading || balanceQuery.isLoading || reconciliationQuery.isLoading,
    error: entriesQuery.error || balanceQuery.error || reconciliationQuery.error,
    refetch: () => {
      entriesQuery.refetch();
      balanceQuery.refetch();
      reconciliationQuery.refetch();
    },
  };
}
